import { RUN_LINE_MAX_CHARS, type ActiveRunDto } from '@aiai/shared';
import { agentTagForRun, isWorkingRun } from './layout';

export type FeedTag = 'E' | 'PM' | 'OK';
export interface FeedEntry {
  id: string;
  /** Wall-clock ms (Date.now()) the line was observed. */
  at: number;
  tag: FeedTag;
  code: string | null;
  text: string;
  href: string | null;
}
export const FEED_MAX = 30;

export type FeedRun = Pick<ActiveRunDto, 'id' | 'issueCode' | 'lane' | 'currentStageRole' | 'lastLogLine' | 'status' | 'awaitingGate'>;

export function hashText(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/**
 * The one feed id for a run's display line, shared by the live path (the
 * gateway's already trimmed + cut `run.line` text) and the poll path (the
 * persisted full `lastLogLine`), so both collapse to a single feed entry.
 * Normalises exactly as the gateway does — trim(), then slice(0, 160) — plus a
 * trailing trimEnd() so the result is idempotent: re-normalising the gateway's
 * text (whose cut may end on whitespace) yields the same key as the full line.
 */
export function lineId(runId: string, text: string): string {
  return `line:${runId}:${hashText(text.trim().slice(0, RUN_LINE_MAX_CHARS).trimEnd())}`;
}

/** The feed's words for an agent that began working, by lane (live, poll and replay alike). */
export function startedText(lane: 'plan' | 'build' | 'chat'): string {
  return lane === 'chat' ? 'started answering' : lane === 'plan' ? 'started planning' : 'started building';
}

/**
 * Feed lines from two consecutive run snapshots (honest: only working runs).
 * First snapshot (prev undefined): each working agent's current line.
 * Later: "started …" when a run begins working, plus every new display line.
 */
export function feedFromRuns<R extends FeedRun>(
  prev: R[] | undefined,
  next: R[],
  now: number,
  hrefFor: (r: R) => string | null,
): FeedEntry[] {
  const before = new Map((prev ?? []).map((r) => [r.id, r]));
  const out: FeedEntry[] = [];
  for (const run of next) {
    if (!isWorkingRun(run)) continue;
    const old = before.get(run.id);
    const tag = agentTagForRun(run);
    const base = { tag, code: run.issueCode, href: hrefFor(run) };
    if (prev && (!old || !isWorkingRun(old))) {
      out.push({ ...base, id: `start:${run.id}`, at: now, text: startedText(run.lane ?? (tag === 'PM' ? 'plan' : 'build')) });
    }
    if (run.lastLogLine && run.lastLogLine !== old?.lastLogLine) {
      out.push({ ...base, id: lineId(run.id, run.lastLogLine), at: now, text: run.lastLogLine });
    }
  }
  return out;
}

/** Drops every entry whose id starts with `prefix` (e.g. the `replay:` lines once a replay is closed). */
export function removeFeedByPrefix(prev: FeedEntry[], prefix: string): FeedEntry[] {
  return prev.some((e) => e.id.startsWith(prefix)) ? prev.filter((e) => !e.id.startsWith(prefix)) : prev;
}

/** Newest first: the last fresh line ends up on top; ids are unique; capped. */
export function mergeFeed(prev: FeedEntry[], fresh: FeedEntry[], max = FEED_MAX): FeedEntry[] {
  const seen = new Set<string>();
  const out: FeedEntry[] = [];
  for (const e of [...[...fresh].reverse(), ...prev]) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    out.push(e);
  }
  return out.slice(0, max);
}
